import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ManagedJobProfileTemplateResponse } from '../../../../../data-contract/job-profile-template';
import { useApplicationFetchers } from '../../../../../fetchers/use-application-fetchers';
import { companySettingsKeys } from '../../../../api/query-keys';

const getLatestManagedTemplate = (
    templates: readonly ManagedJobProfileTemplateResponse[],
): ManagedJobProfileTemplateResponse | null => {
    if (templates.length === 0) {
        return null;
    }

    const sorted = [...templates].sort((a, b) => new Date(b.activatedAt).getTime() - new Date(a.activatedAt).getTime());
    return sorted[0] ?? null;
};

const findDefaultManagedTemplate = (
    templates: readonly ManagedJobProfileTemplateResponse[],
): ManagedJobProfileTemplateResponse | null => templates.find((template) => template.isDefault) ?? null;

export const getDefaultManagedTemplate = (
    templates: readonly ManagedJobProfileTemplateResponse[],
): ManagedJobProfileTemplateResponse | null =>
    findDefaultManagedTemplate(templates) ?? getLatestManagedTemplate(templates);

export const resolveManagedTemplateToActivate = (
    templates: readonly ManagedJobProfileTemplateResponse[],
): ManagedJobProfileTemplateResponse | null =>
    findDefaultManagedTemplate(templates) ?? (templates.length === 1 ? (templates[0] ?? null) : null);

export const findManagedTemplateById = (
    templates: readonly ManagedJobProfileTemplateResponse[],
    templateId: string | null | undefined,
): ManagedJobProfileTemplateResponse | null => {
    if (is.nullOrUndefined(templateId)) {
        return null;
    }

    return templates.find((template) => template.id === templateId) ?? null;
};

const useManagedTemplatesQueryOptions = () => {
    const { fetchManagedJobProfileTemplates } = useApplicationFetchers();

    return {
        queryFn: async (): Promise<readonly ManagedJobProfileTemplateResponse[]> => {
            const result = await fetchManagedJobProfileTemplates();

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: companySettingsKeys.managedJobProfileTemplates(),
        staleTime: 60_000,
        throwOnError: true,
    };
};

export const useManagedTemplates = (
    enabled: boolean = true,
): UseQueryResult<readonly ManagedJobProfileTemplateResponse[], Error> => {
    const queryOptions = useManagedTemplatesQueryOptions();

    return useQuery({ ...queryOptions, enabled });
};

export const useManagedTemplate = (
    enabled: boolean = true,
): UseQueryResult<ManagedJobProfileTemplateResponse | null, Error> => {
    const queryOptions = useManagedTemplatesQueryOptions();

    return useQuery({ ...queryOptions, enabled, select: getDefaultManagedTemplate });
};
